import { captureException } from '@sentry/react';

import { ApiError, FetchResponse } from './types';
import { getEnv } from './utils/getEnv';

let errorListeners: ((errorObj: APIError) => void)[] = [];

export function registerFetchErrorListener(listener: (errorObj: APIError) => void) {
  errorListeners.push(listener);
}

export function deregisterFetchErrorListener(listener: (errorObj: APIError) => void) {
  errorListeners = errorListeners.filter((item) => item !== listener);
}

export const e2eTestHeaders: Record<string, string> = {};

export const turnstileHeaders: Record<string, string> = {};

// Initialize e2e test header if the secret is provided
if (process.env.NEXT_PUBLIC_BYPASS_TURNSTILE_SECRET) {
  turnstileHeaders['X-E2e-Test'] = process.env.NEXT_PUBLIC_BYPASS_TURNSTILE_SECRET;
}

const baseUrl = getEnv(
  'NEXT_PUBLIC_CLOUDQUERY_API_BASE_URL',
  process.env.NEXT_PUBLIC_CLOUDQUERY_API_BASE_URL,
  'http://localhost:4444',
);

export interface APIError {
  data: any;
  message: string;
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  payload: any;
  status: number;
}

/**
 * Since react-query is generated via orval we cannot
 * provide default value to pageParam. Therefore, we assign
 * default value to page param here
 */
function handlePageParam(params: Record<string, any> | undefined) {
  const updatedParams = { ...params };
  if (Object.hasOwn(updatedParams, 'page')) {
    updatedParams.page = updatedParams.page || 1;
  }

  return updatedParams;
}

interface CustomFetchConfig {
  // eslint-disable-next-line no-undef
  body?: RequestInit['body'];
  credentials?: 'include' | 'omit' | 'same-origin';
  // eslint-disable-next-line no-undef
  headers?: RequestInit['headers'];
  method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  params?: Record<string, any>;
  signal?: AbortSignal | null;
  streamData?: boolean;
}

export const getFullUrl = (url: string, searchParamsStr: string) => {
  const urlWithSearchParams = `${url}${searchParamsStr ? `?${searchParamsStr}` : ''}`;
  const origin = typeof window !== 'undefined' ? window.location.origin : undefined;
  const fullUrl = new URL(
    `${/^https?:\/\//.test(url) ? '' : baseUrl}${urlWithSearchParams}`,
    typeof origin === 'string' && /^https?:\/\//.test(origin) ? origin : undefined,
  );

  return fullUrl;
};

export const customFetch = async <T>(
  url: string,
  config: CustomFetchConfig,
): Promise<FetchResponse<T>> => {
  const { body: data, headers, method, params, signal, streamData } = config;

  const searchParams = handlePageParam(params);
  let searchParamsStr = '';
  for (const [key, value] of Object.entries(searchParams || {})) {
    searchParamsStr += Array.isArray(value)
      ? `${(value as string[]).map((v) => `${key}=${encodeURIComponent(v)}`).join('&')}&`
      : `${key}=${encodeURIComponent(value as string)}&`;
  }

  const fullUrl = getFullUrl(url, searchParamsStr);

  // The idea behind appending the commit SHA is to bust caches
  // on deployments so that every new deployment gets fresh data.
  if (
    process.env.APP_NAME &&
    ['hub', 'website'].includes(process.env.APP_NAME) &&
    process.env.VERCEL_GIT_COMMIT_SHA
  ) {
    fullUrl.searchParams.append('_b', process.env.VERCEL_GIT_COMMIT_SHA);
  }

  try {
    const response = await fetch(fullUrl.toString(), {
      method,
      ...(data ? { body: typeof data === 'string' ? data : JSON.stringify(data) } : {}),
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...headers,
        ...(process.env.NEXT_PUBLIC_PLAYWRIGHT_TEST === 'true' ? e2eTestHeaders : {}),
        ...turnstileHeaders,
      },
      signal,
    });

    const responseData: any = streamData ? {} : await response.json().catch(() => ({}));

    if (response.ok && response.status !== 205) {
      return {
        data: responseData as T,
        headers: response.headers,
        response,
        status: response.status,
        statusText: response.statusText,
      };
    }

    throw new ApiError(`There was an error during the API request`, {
      data: streamData ? await response.json().catch(() => ({})) : responseData,
      headers: response.headers,
      payload: data,
      status: response.status,
    });
  } catch (error) {
    const isUserEndpointError =
      new URL(fullUrl, 'https://cloudquery.io').pathname.endsWith('/user') && error.status === 401;
    const isServerSide404 = typeof window === 'undefined' && error.status === 404;

    if (!isUserEndpointError && !isServerSide404 && error.name !== 'AbortError') {
      // eslint-disable-next-line no-console
      console.error(`captured API error for [${method}] ${fullUrl}:`, error);
      const extra =
        error instanceof ApiError
          ? {
              data: error.data,
              message: error.message,
              method,
              payload: error.payload,
              status: error.status,
              url: fullUrl,
            }
          : {
              error: {
                message: error.message,
                method,
                name: error.name,
                payload: data,
                stack: error.stack,
                url: fullUrl,
              },
            };
      captureException(
        error instanceof ApiError
          ? error
          : new ApiError(`API Error for ${fullUrl}: ${error.message}`, {
              data: undefined,
              payload: data,
              status: 0,
            }),
        {
          extra,
        },
      );
    }

    for (const callback of errorListeners) {
      callback(error);
    }

    throw error;
  }
};

export default customFetch;
